"use client";

/**
 * The contact detail header's avatar. Read-only viewers see the avatar; staff
 * who can edit get the avatar itself as the control — one click opens a two-
 * item menu (Upload/Replace, Remove). Minimal by design (MARKETING_MINIMALISM):
 * no dialog, no cropper; the server square-crops and re-encodes.
 *
 * The new pointer is held locally after a change so the header updates at once,
 * without waiting for the detail query to refetch. Mount with `key={contactId}`
 * so moving to another contact drops that override.
 */

import React, { useCallback, useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ACCEPTED_AVATAR_UPLOAD_TYPES,
  avatarFileProblem,
  removeContactAvatar,
  uploadContactAvatar,
} from "@/lib/contacts/avatar/client-upload";
import { extractErrorMessage } from "@west-tennessee-consulting/api-errors";
import { ContactAvatar } from "./ContactAvatar";

export interface ContactAvatarEditorProps {
  contactId: string;
  name?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
  canEdit: boolean;
}

export function ContactAvatarEditor({ contactId, name, email, avatarUrl, canEdit }: ContactAvatarEditorProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  /** `undefined` = no local change; `null` = removed here. */
  const [override, setOverride] = useState<string | null | undefined>(undefined);
  const current = override !== undefined ? override : (avatarUrl ?? null);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["contacts"] });
  }, [queryClient]);

  const onFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = ""; // picking the same file again must still fire
      if (!file) return;
      const problem = avatarFileProblem(file);
      if (problem) {
        toast.error(problem === "type" ? "Use a JPEG, PNG or WebP image." : "The image is larger than 5 MB.");
        return;
      }
      setBusy(true);
      try {
        setOverride(await uploadContactAvatar(contactId, file));
        refresh();
        toast.success("Photo updated");
      } catch (error) {
        toast.error(extractErrorMessage(error, { fallback: "Could not upload that photo." }));
      } finally {
        setBusy(false);
      }
    },
    [contactId, refresh],
  );

  const onRemove = useCallback(async () => {
    setBusy(true);
    try {
      await removeContactAvatar(contactId);
      setOverride(null);
      refresh();
      toast.success("Photo removed");
    } catch (error) {
      toast.error(extractErrorMessage(error, { fallback: "Could not remove that photo." }));
    } finally {
      setBusy(false);
    }
  }, [contactId, refresh]);

  const avatar = (
    <ContactAvatar name={name} email={email} avatarUrl={current} seed={contactId} size="lg" />
  );
  if (!canEdit) return <div className="flex-shrink-0">{avatar}</div>;

  return (
    <div className="flex-shrink-0">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_AVATAR_UPLOAD_TYPES.join(",")}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => void onFile(e)}
        data-testid="contact-avatar-file-input"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={busy}
            className="group relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={current ? "Change contact photo" : "Add contact photo"}
            data-testid="contact-avatar-edit-trigger"
          >
            {avatar}
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              {busy ? <Loader2 className="size-5 animate-spin" /> : <Camera className="size-5" />}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
            {current ? "Replace photo" : "Upload photo"}
          </DropdownMenuItem>
          {current ? (
            <DropdownMenuItem onSelect={() => void onRemove()} className="text-destructive focus:text-destructive">
              Remove photo
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
